import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { 
  StyleSheet,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  View,
  Text,
  TouchableOpacity,
} from 'react-native';
import { showConfirm } from '@/utils/common/alert';
import { StackScreenProps } from '@react-navigation/stack';

import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { BaseAdminScreen } from '@/components';
import { ADMIN_COLORS } from '@/constants/admin';
import { useUpdateTaskMutation, useGetTaskDetailsQuery, useDeleteTaskMutation } from '@/store/api/admin/tasksApi';
import { useAdminUser } from '@/hooks/admin/useAdminUser';
import { useToast, useErrorToast } from '@/contexts/admin/ToastContext';
import { useCreateTaskForm } from '@/components/admin/tasks/CreateTask/hooks/useCreateTaskForm';
import { ActionButtons, DateTimePickerModal } from '@/components';
import { TaskAssigneesSection, TaskDetailsSection, TaskSettingsSection, TaskSubtasksSection, TaskFilesSection } from '@/components/admin/tasks/CreateTask/components';
import { useDatePicker } from '@/components/admin/events/CreateEvent/hooks/useDatePicker';
import { ITaskItemAttributes, ITaskFormValues } from '@/types/admin/tasks';
import { useResponsive } from '@/hooks/common/useResponsive';
import { DesktopFormPage } from '@/components/common/admin/desktop/DesktopFormPage';
import { WebConfirmModal } from '@/components/common/modals';
import { DESKTOP_CONFIRM_COPY } from '@/constants/admin/desktop';

type EditTaskScreenProps = StackScreenProps<AdminStackParamList, 'EditTask'>;

export const EditTaskScreen: React.FC<EditTaskScreenProps> = ({
  navigation,
  route,
}) => {
  const { layoutMode } = useResponsive();
  const { taskId } = route.params;
  const { showSuccess } = useToast();
  const { showApiError } = useErrorToast();
  const adminUser = useAdminUser();
  const selectedPropertyId = adminUser.selectedPropertyId;
  
  const [updateTask, { isLoading: isUpdating }] = useUpdateTaskMutation();
  const [deleteTask, { isLoading: isDeleting }] = useDeleteTaskMutation();
  const { 
    data: taskDetails, 
    isLoading: isLoadingTask, 
    error: taskError,
    refetch: refetchTask 
  } = useGetTaskDetailsQuery({ taskId });

  // Custom hooks for form and date picker logic
  const {
    formData,
    errors,
    validateForm,
    handleFieldChange,
    showDiscardAlert,
    hasUnsavedChanges,
    initializeForm,
  } = useCreateTaskForm();

  const {
    datePickerState,
    DateTimePicker,
    handleDatePickerChange,
    showStartDatePicker,
    hideDatePickers,
    confirmDateSelection,
    showFallbackDatePicker,
    setDatePickerMode,
  } = useDatePicker();

  // Web-only: anchor for date/time dropdown positioning
  const [webAnchor, setWebAnchor] = useState<any | null>(null);

  // Initialize form with task data when task loads
  useEffect(() => {
    const task: any = (taskDetails as any)?.data ?? taskDetails;
    if (task) {
      const initialFormData: ITaskFormValues = {
        title: task.title || '',
        description: task.description || '',
        taskStatus: task.taskStatus,
        priority: task.priority,
        category: (task.category as any) || 'maintenance',
        location: task.location || '',
        dueDate: task.dueDate ? new Date(task.dueDate) : null,
        assignees: task.assignees || [],
        subtasks: task.subtasks || [],
        isBlocked: task.isBlocked || false,
        isPrivate: task.isPrivate || false,
        files: task.files || [],
      };
      initializeForm(initialFormData);
    }
  }, [taskDetails, initializeForm]);

  // Date selection handler
  const handleDateSelection = useCallback((date: Date | null) => {
    handleFieldChange('dueDate', date);
  }, [handleFieldChange]);

  // Due date picker handler with fallback support
  const handleDueDatePress = useCallback((anchor?: any) => {
    const currentDate = formData.dueDate ? new Date(formData.dueDate) : new Date();
    if (Platform.OS === 'web') {
      setWebAnchor(anchor || null);
      showStartDatePicker(currentDate);
      return;
    }
    if (DateTimePicker) {
      showStartDatePicker(currentDate);
    } else {
      showFallbackDatePicker('Due Date', currentDate, (date: Date) => {
        handleDateSelection(date);
      });
    }
  }, [Platform.OS, formData.dueDate, showStartDatePicker, DateTimePicker, showFallbackDatePicker, handleDateSelection]);

  // Handle date picker confirmation
  const handleDatePickerConfirmation = useCallback((pickerType: 'start' | 'end' | 'publish') => {
    confirmDateSelection(pickerType, (date: Date) => {
      handleDateSelection(date);
    });
  }, [confirmDateSelection, handleDateSelection]);

  // Clear due date handler
  const handleClearDueDate = useCallback(() => {
    handleFieldChange('dueDate', null);
  }, [handleFieldChange]);

  // Form submission
  const handleSubmit = useCallback(async () => {
    if (!selectedPropertyId) {
      showApiError('No property selected');
      return;
    }

    // Validate but allow past due dates on edit (align with admin-web behavior)
    const validationErrors = validateForm();
    const { dueDate: _ignoreDueDateError, ...otherErrors } = validationErrors as any;
    const remainingErrors = Object.keys(otherErrors).filter((k) => !!(otherErrors as any)[k]);
    if (remainingErrors.length > 0) {
      showApiError('Please fix the form errors before submitting');
      return;
    }

    try {
      
      await updateTask({
        taskId,
        taskForm: formData,
        selectedPropertyId,
        files: formData.files || [],
      }).unwrap();

      showSuccess('Task updated successfully!');
      navigation.goBack();
    } catch (error: any) {
      console.error('Failed to update task:', error);
      showApiError(error?.data?.message || 'Failed to update task');
    }
  }, [selectedPropertyId, formData, validateForm, updateTask, taskId, showSuccess, showApiError, navigation]);

  // Delete handler with cross-platform confirmation
  const handleDelete = useCallback(async () => {
    const confirmed = await showConfirm(
      'Delete Task',
      'Are you sure you want to delete this task? This action cannot be undone.',
      'Delete',
      'Cancel'
    );
    
    if (!confirmed) return;
    
    try {
      await deleteTask({ taskId }).unwrap();
      showSuccess('Task deleted successfully!');
      navigation.goBack();
    } catch (error: any) {
      console.error('Failed to delete task:', error);
      showApiError(error?.data?.message || 'Failed to delete task');
    }
  }, [taskId, deleteTask, showSuccess, showApiError, navigation]);

  // Web delete confirm modal state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Web cancel confirm modal state
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const confirmDeleteWeb = useCallback(async () => {
    try {
      await deleteTask({ taskId }).unwrap();
      showSuccess('Task deleted successfully!');
      setShowDeleteConfirm(false);
      navigation.goBack();
    } catch (error: any) {
      console.error('Failed to delete task:', error);
      showApiError(error?.data?.message || 'Failed to delete task');
      setShowDeleteConfirm(false);
    }
  }, [deleteTask, taskId, showSuccess, showApiError, navigation]);

  // Navigation handlers
  const handleCancel = useCallback(() => {
    if (Platform.OS === 'web') {
      // Desktop/web confirmation is handled by DesktopFormPage
      navigation.goBack();
      return;
    }
    // Mobile: always confirm, even without edits
    showDiscardAlert(() => navigation.goBack());
  }, [showDiscardAlert, navigation]);

  // Handle loading and error states
  if (isLoadingTask) {
    return (
      <BaseAdminScreen
        screenName="EditTask"
        navigation={navigation}
        showPlaceholder={true}
      />
    );
  }

  const effectiveTask: any = (taskDetails as any)?.data ?? taskDetails;
  if (taskError || !effectiveTask) {
    return (
      <BaseAdminScreen
        screenName="EditTask"
        navigation={navigation}
        showPlaceholder={true}
      />
    );
  }

  // Reusable scroll content for both layouts
  const scrollContent = (
    <>
      <TaskDetailsSection
        formData={formData}
        errors={errors}
        onFieldChange={handleFieldChange}
      />
      <TaskSettingsSection
        formData={formData}
        errors={errors}
        onFieldChange={handleFieldChange}
        onDueDatePress={handleDueDatePress}
        onClearDueDate={handleClearDueDate}
      />
      <TaskAssigneesSection
        formData={formData}
        errors={errors}
        onFieldChange={handleFieldChange}
      />
      <TaskSubtasksSection
        formData={formData}
        errors={errors}
        onFieldChange={handleFieldChange}
      />
      <TaskFilesSection
        formData={formData}
        errors={errors}
        onFieldChange={handleFieldChange}
      />
    </>
  );

  if (layoutMode === 'desktop') {
    return (
      <>
        {/* Web Cancel Confirm */}
        <WebConfirmModal
          visible={Platform.OS === 'web' && showCancelConfirm}
          title={DESKTOP_CONFIRM_COPY.discardTitle}
          message={DESKTOP_CONFIRM_COPY.discardMessage}
          confirmText={DESKTOP_CONFIRM_COPY.discardConfirmText}
          cancelText={DESKTOP_CONFIRM_COPY.discardCancelText}
          onConfirm={() => { setShowCancelConfirm(false); navigation.goBack(); }}
          onCancel={() => setShowCancelConfirm(false)}
        />
        {/* Web Delete Confirm */}
        <WebConfirmModal
          visible={Platform.OS === 'web' && showDeleteConfirm}
          title={'Delete Task'}
          message={'Delete this task? This action is permanent.'}
          confirmText={'Delete'}
          cancelText={'Cancel'}
          onConfirm={confirmDeleteWeb}
          onCancel={() => setShowDeleteConfirm(false)}
        />
        <DesktopFormPage
          title="Edit Task"
          subtitle="Update task details"
          isSubmitting={isUpdating || isDeleting}
          submitText="Update"
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          confirmDiscardOnWeb={true}
          renderHeaderActions={() => (
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <TouchableOpacity onPress={() => setShowCancelConfirm(true)} style={desktopHeaderStyles.secondaryBtn}>
                <Text style={desktopHeaderStyles.secondaryText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => {
                  if (Platform.OS === 'web') {
                    setShowDeleteConfirm(true);
                  } else {
                    handleDelete();
                  }
                }}
                disabled={isDeleting}
                style={[desktopHeaderStyles.deleteBtn, isDeleting ? { opacity: 0.7 } : null]}
              >
                <Text style={desktopHeaderStyles.deleteText}>Delete</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={handleSubmit}
                disabled={isUpdating}
                style={[desktopHeaderStyles.primaryBtn, isUpdating ? { opacity: 0.7 } : null]}
              >
                <Text style={desktopHeaderStyles.primaryText}>Update</Text>
              </TouchableOpacity>
            </View>
          )}
        >
          {scrollContent}
        </DesktopFormPage>

        <DateTimePickerModal
          DateTimePicker={DateTimePicker}
          datePickerState={datePickerState}
          onDateChange={handleDatePickerChange}
          onConfirm={handleDatePickerConfirmation}
          onCancel={hideDatePickers}
          pickerType="start"
          title="Due Date"
          onModeChange={setDatePickerMode}
          webAnchor={webAnchor}
          dateOnly={true}
        />
      </>
    );
  }

  return (
    <>
      <BaseAdminScreen
        screenName="EditTask"
        navigation={navigation}
        showPlaceholder={false}
        scrollEnabled={false}
        contentContainerStyle={styles.baseScreenContent}
      >
        <KeyboardAvoidingView
          style={styles.container}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          keyboardVerticalOffset={Platform.OS === 'ios' ? 100 : 0}
        >
          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            {scrollContent}
          </ScrollView>

          <ActionButtons
            isLoading={isUpdating || isDeleting}
            onCancel={handleCancel}
            onSubmit={handleSubmit}
            onDelete={handleDelete}
            submitText="Update"
            cancelText="Cancel"
            deleteText="Delete"
            showDelete={true}
          />
        </KeyboardAvoidingView>
      </BaseAdminScreen>

      <DateTimePickerModal
        DateTimePicker={DateTimePicker}
        datePickerState={datePickerState}
        onDateChange={handleDatePickerChange}
        onConfirm={handleDatePickerConfirmation}
        onCancel={hideDatePickers}
        pickerType="start"
        title="Due Date"
        onModeChange={setDatePickerMode}
        webAnchor={webAnchor}
        dateOnly={true}
      />
    </>
  );
};

const styles = StyleSheet.create({
  baseScreenContent: {
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: 20,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    flexGrow: 1,
  },
  container: {
    flex: 1,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 90, // Space for action buttons
  },
});

const desktopHeaderStyles = StyleSheet.create({
  secondaryBtn: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
  },
  secondaryText: {
    color: ADMIN_COLORS.TEXT_PRIMARY,
    fontWeight: '600',
  },
  primaryBtn: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: ADMIN_COLORS.PRIMARY,
  },
  primaryText: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
  deleteBtn: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: ADMIN_COLORS.ERROR,
  },
  deleteText: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
});
